/**
 * 🧮 수량 세기 계산기 — 주요재고 확인의 품목 카드·조치 카드 안에 펼친다 (v2.207.0 · 2026-09-28)
 *
 * 사용자 요청: 「박스단위로 숫자가 세기 힘든게 있어서 … 사칙연산 가능하도록 계산기 기능을 넣어줘」
 *   예) 한 박스 20개짜리 4박스 + 낱개 7 → 4 × 20 + 7 = 87
 *
 * ⭐새 창(모달)이 아니라 **카드 안에 펼친다** — 이 화면은 새 창을 만들지 않는다(입력은 카드 안에서).
 *   그래서 계산기를 연 채로도 아래 고정 단추([✅ 확인하고 다음 →] 등)를 그대로 누를 수 있다.
 * ⭐답을 **기준 수량과 바로 견준다**(「현재고 87 — 같습니다」·「3개 적음」) — 「숫자를 알려주고
 *   세는 게 좋다」(사용자 09-23)와 같은 방향이다.
 * ⭐답을 수량 칸으로 넘기는 단추는 **부르는 쪽이 넘길 곳을 줄 때만** 그린다(onApply).
 *   계산기는 기록을 스스로 남기지 않는다 — 저장은 늘 원래 단추로 한다.
 * ⭐표시판 높이를 고정했다 — 답·견주기 줄이 생기고 없어져도 **키 자리가 움직이지 않는다**
 *   (창고에서 빠르게 누르다 엉뚱한 키가 눌리지 않게). 넘기기 단추는 키패드 **아래**에 둔다.
 * ⭐상태는 부르는 쪽이 갖는다 — 품목 카드는 품목마다 식을 따로 기억한다(← 이전으로 돌아가도 남는다).
 *   키는 「직전 상태 → 다음 상태」 함수로 넘긴다(setState 의 함수 모양) — 빠르게 두 번 눌러도 안 샌다.
 *
 * 📱 폰에서 손가락으로 (사용자 09-28 「모바일로 사용하는점 고려해서 … 터치로 계산 편하게」)
 *   · **입력칸이 없다** — 전용 키패드만 쓴다. 폰 키보드가 올라와 화면을 가리지 않는다.
 *   · 키 높이는 화면 높이에 맞춰 **48~64px**(clamp · 7vh) — 이 앱은 화면을 0.85배로 그리므로(index.html)
 *     실제로는 약 41~54px. 48 은 아주 낮은 화면(높이 690 미만)에서만 — 폭이 약 85px 라 누를 자리는 넉넉하다
 *     (폰 글자판 키보다 크다). 한 줄 4칸이 카드 폭을 꽉 채운다.
 *     ⚠64px 고정이면 360×640 폰에서 키패드를 다 보이게 내릴 때 **품목 이름이 머리글 밑으로 가려졌다**
 *     (실측) — 무엇을 세는 중인지 안 보이면 안 된다. 작은 폰에서만 키가 조금 낮아진다.
 *   · 누르는 순간 색이 바뀌고 살짝 눌린다 — 눌렸는지 눈으로 바로 안다.
 *   · 빠르게 두 번 눌러도 화면이 확대되지 않고(touch-manipulation), 길게 눌러도 글자 선택·
 *     복사 메뉴가 뜨지 않는다.
 *   · 표시판 높이를 고정해 답이 생겨도 키 자리가 그대로다(위 ⭐).
 */
import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { calcPress, calcEvaluate, calcCompare, calcExprView, calcNumberView } from '../lib/count-calc';
import type { CalcKey, CalcState } from '../lib/count-calc';

type Tone = 'num' | 'op' | 'fn' | 'eq';
interface KeyDef {
  k: CalcKey;
  label: string;
  aria: string;
  tone: Tone;
  /** 두 칸짜리 키 (= 는 세로 두 칸 · 0 은 가로 두 칸) */
  span?: string;
}

const digit = (n: string): KeyDef => ({ k: n as CalcKey, label: n, aria: n, tone: 'num' });

/** 폰 계산기 배치 — 4줄 × 4칸 + 맨 아래 0 · 소수점 */
const KEYS: KeyDef[] = [
  { k: 'C', label: 'C', aria: '모두 지우기', tone: 'fn' },
  { k: 'back', label: '⌫', aria: '한 글자 지우기', tone: 'fn' },
  { k: '/', label: '÷', aria: '나누기', tone: 'op' },
  { k: '*', label: '×', aria: '곱하기', tone: 'op' },
  digit('7'), digit('8'), digit('9'),
  { k: '-', label: '−', aria: '빼기', tone: 'op' },
  digit('4'), digit('5'), digit('6'),
  { k: '+', label: '+', aria: '더하기', tone: 'op' },
  digit('1'), digit('2'), digit('3'),
  { k: '=', label: '=', aria: '계산', tone: 'eq', span: 'row-span-2' },
  { ...digit('0'), span: 'col-span-2' },
  { k: '.', label: '.', aria: '소수점', tone: 'num' },
];

const TONE: Record<Tone, string> = {
  num: 'bg-white border-slate-300 text-slate-900 active:bg-slate-100',
  op: 'bg-blue-50 border-blue-200 text-blue-700 active:bg-blue-100',
  fn: 'bg-slate-200 border-slate-300 text-slate-700 active:bg-slate-300',
  eq: 'bg-blue-600 border-blue-600 text-white active:bg-blue-700',
};

/** 길게 눌러도 복사 메뉴(아이폰)·회색 번쩍임(안드로이드)이 안 뜨게 — 누름 표시는 active 색으로 한다 */
const KEY_STYLE: CSSProperties = { WebkitTouchCallout: 'none', WebkitTapHighlightColor: 'transparent' };

function fmt(n: number): string {
  return n.toLocaleString('ko-KR');
}

export interface CountCalculatorProps {
  state: CalcState;
  /** setState 와 같은 모양 — 직전 상태를 받아 다음 상태를 돌려주는 함수를 넘긴다 */
  onChange: (update: (prev: CalcState) => CalcState) => void;
  /** 견줄 수량 — 품목 카드는 현재고, 조치 카드는 지금 시스템 */
  refQty: number;
  refLabel: string;
  /** 답을 수량 칸으로 넘기는 단추의 글 — onApply 와 짝. 둘 중 하나라도 없으면 단추를 안 그린다 */
  applyLabel?: (qty: number) => string;
  onApply?: (qty: number) => void;
  /** 답이 기준과 같아도 넘기기 단추를 보일지 (수량 칸이 이미 열려 있을 때) */
  applyWhenSame?: boolean;
}

export function CountCalculator({
  state,
  onChange,
  refQty,
  refLabel,
  applyLabel,
  onApply,
  applyWhenSame = false,
}: CountCalculatorProps) {
  const view = calcExprView(state.expr);
  const r = calcEvaluate(state.expr);
  const cmp = calcCompare(r, refQty);

  // 긴 식은 끝(방금 누른 자리)이 보이게
  const exprRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = exprRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [view]);

  const applyQty =
    cmp.kind === 'less' || cmp.kind === 'more' || (cmp.kind === 'same' && applyWhenSame) ? cmp.value : null;

  return (
    <div className="rounded border border-slate-200 bg-slate-50 p-2" data-count-calc="">
      {/* 표시판 — 세 줄 모두 높이 고정 */}
      <div className="rounded bg-white border border-slate-200 px-3 py-1.5">
        <div
          ref={exprRef}
          className="h-7 leading-7 overflow-x-auto whitespace-nowrap text-right text-lg text-slate-500 tabular-nums"
          data-calc-expr=""
        >
          {view || <span className="text-slate-300">예) 4 × 20 + 7</span>}
        </div>
        <div className="h-10 leading-10 text-right tabular-nums whitespace-nowrap overflow-hidden" aria-live="polite" data-calc-result="">
          {r.kind === 'ok' && <span className="text-3xl font-bold text-slate-900">= {calcNumberView(r.value)}</span>}
          {r.kind === 'error' && <span className="text-sm font-semibold text-red-600">{r.message}</span>}
        </div>
        <div className="h-6 leading-6 text-right text-sm truncate" data-calc-compare="">
          {cmp.kind === 'same' && (
            <span className="font-semibold text-emerald-700">✓ {refLabel} {fmt(refQty)} — 같습니다</span>
          )}
          {cmp.kind === 'less' && (
            <span className="font-semibold text-amber-700">
              {refLabel} {fmt(refQty)}보다 {fmt(cmp.gap)}개 적음
            </span>
          )}
          {cmp.kind === 'more' && (
            <span className="font-semibold text-amber-700">
              {refLabel} {fmt(refQty)}보다 {fmt(cmp.gap)}개 많음
            </span>
          )}
          {cmp.kind === 'bad' && <span className="text-slate-500">{cmp.message}</span>}
        </div>
      </div>

      {/* 키패드 */}
      <div className="mt-2 grid grid-cols-4 auto-rows-[clamp(3rem,7vh,4rem)] gap-1.5">
        {KEYS.map((d) => (
          <button
            key={d.k}
            type="button"
            aria-label={d.aria}
            onClick={() => onChange((prev) => calcPress(prev, d.k))}
            style={KEY_STYLE}
            className={`rounded border text-[28px] font-semibold select-none touch-manipulation transition-transform duration-75 active:scale-95 ${TONE[d.tone]} ${d.span ?? ''}`}
          >
            {d.label}
          </button>
        ))}
      </div>

      {onApply && applyLabel && applyQty !== null && (
        <button
          type="button"
          onClick={() => onApply(applyQty)}
          className="w-full mt-2 min-h-[44px] rounded border border-amber-400 bg-amber-50 text-sm font-semibold text-amber-800 active:bg-amber-100"
        >
          {applyLabel(applyQty)}
        </button>
      )}
    </div>
  );
}
